import axios from 'axios';
import { useEffect, useRef, useState, useCallback } from 'react';
import getCache from 'cache/cache';
import isLoggedIn from 'utils/isLoggedIn';

import type { GetServerSideProps } from 'next';
import type { ShowroomAppProps } from 'pages/_app';
import type { NextPage } from 'next';
import type { SearchResult } from 'types/SearchResult.interface';
import type { Content } from 'types/Content.interface';

import Layout from 'layouts/Layout';
import TemplateFactory from 'templates/TemplateFactory';
import Dialog from 'components/Dialog';
import Symbol from 'components/Symbol';
import Badge, { Tag } from 'components/Badge';
import ShowCard, { ShowCardPlace } from 'components/ShowCard';
import CategoryCard from 'components/CategoryCard';
import BrandCard from 'components/BrandCard';

import Search from 'icons/Search';
import Filter from 'icons/Filter';
import Spinner from "icons/Spinner";
import { useRouter } from "next/router";

export const getServerSideProps: GetServerSideProps = async ({ req, res, query }) => {
  const isAuthenticated = isLoggedIn(req, res);
  try {
    const content = await getCache<Content>(
      `${process.env.API_URI}/api/v1/pages/shows`,
      isAuthenticated,
      req.headers.cookie as string
    );
    try {
      // define params for search request
      const params = {}
      for (let param in query) {
        (params as any)[param] = query[param];
      }
      const queryRequest = Object.keys(query).length ? '?' + new URLSearchParams(params) : ''

      const search = await getCache<SearchResult>(
        `${process.env.API_URI}/api/v1/shows/search${queryRequest}`,
        isAuthenticated,
        req.headers.cookie as string
      );
      res.setHeader('x-cache-next', content.xCacheNext);
      return {
        props: {
          ...content.data,
          ...search?.data,
        },
      };
    } catch (error) {

    }
  } catch (error: any) {
    return {
      props: {}
    }
  }
  return {
    props: {}
  }
};

interface PageProps extends ShowroomAppProps, Content, SearchResult { }

const ShowsPage: NextPage<PageProps> = ({
  isAuthenticated,
  page,
  facets,
  brandsResult,
  categoriesResult,
  showsResult,
}) => {
  // predefine searched categories and brands if they are in the url
  const router = useRouter()
  const query = router.query
  let categoryTags: Tag[] = [],
    brandTags: Tag[] = []

  for (let key in query) {
    switch (key) {
      case 'categories':
        categoryTags = categoriesResult.categories.map(category => {
          return {
            name: category.name,
            value: category.id,
            color: category.color
          }
        })
        break
      case 'brands':
        brandTags = brandsResult.brands.map(brand => {
          return {
            name: brand.name,
            value: brand.id
          }
        })
        break
    }
  }

  const [moreShowsToFetch, setMoreShowsToFetch] = useState(true);
  const [isFetching, setIsFetching] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [pageNumber, setPageNumber] = useState(1);
  const [loadMore, setLoadMore] = useState<boolean>(false);
  const [selectedCategories, setSelectedCategories] = useState<Tag[]>(categoryTags);
  const [selectedBrands, setSelectedBrands] = useState<Tag[]>(brandTags);
  const [showAllBrands, setShowAllBrands] = useState(false);
  const [searchResults, setSearchResults] = useState<SearchResult>({
    brandsResult,
    categoriesResult,
    facets,
    showsResult,
  });

  // Refs
  const formRef = useRef<HTMLFormElement>(null);
  const dialogRef = useRef<React.ElementRef<typeof Dialog>>(null);
  const lastShowObserver = useRef<any>(null)

  const cleanDuplicates = (items: any) => {
    const uniqueIds: any[] = [];

    return items.filter((element: any) => {
      const isDuplicate = uniqueIds.includes(element.id);
      if (!isDuplicate) {
        uniqueIds.push(element.id);
        return true;
      }
      return false;
    });
  }

  // FETCH DATA
  const fetchSearchResults = useCallback(async () => {
    setIsFetching(true);
    const { data } = await axios.post('/api/search', {
      categories: selectedCategories.map((c) => c.value).join(','),
      brands: selectedBrands.map((b) => b.value).join(','),
      query: searchValue,
      page: pageNumber
    });

    //console.table(data);
    setIsFetching(false);

    if (pageNumber === 1) {
      setSearchResults(data)
    } else {
      setSearchResults(prevData => {
        prevData.showsResult.shows = cleanDuplicates(prevData.showsResult.shows.concat(data.showsResult.shows))
        return prevData
      })
    }
    setLoadMore(false);
    setMoreShowsToFetch(data.showsResult.current_page * data.showsResult.per_page < data.showsResult.total);
    //setLoadMore(data.showsResult.current_page * data.showsResult.per_page < data.showsResult.total)
  }, [selectedCategories, selectedBrands, searchValue, pageNumber]);

  const lastShowRef = useCallback((node: any) => {
    if (isFetching) return
    if (lastShowObserver.current) lastShowObserver.current.disconnect()
    lastShowObserver.current = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting && loadMore) {
        setPageNumber(prev => prev + 1)
      }
    })
    if (node) lastShowObserver.current.observe(node)
  }, [isFetching, loadMore])

  // HANDLERS
  const handleClearFilters = () => {
    setSelectedBrands([]);
    setSelectedCategories([]);
    setPageNumber(1)
  };

  const handleSelectFilter = () => {
    setPageNumber(1)
  };

  const handleOnSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    dialogRef.current?.setOpen(false);
    setPageNumber(1)
  };

  useEffect(() => {
    fetchSearchResults()
  }, [selectedCategories, selectedBrands, fetchSearchResults]);

  const showSearchResult = () => {
    let r;
    if (!searchResults.brandsResult.total && !searchResults.categoriesResult.total && !searchResults.showsResult.total) { // Search result is empty
      if (searchValue !== '') { // User has entered a search query
        r = <div className="container pt-16 pb-28 text-center">
          <span className="px-10 py-5 bg-blue-100 rounded-lg block">
            Din sökning - <strong>{searchValue}</strong> - matchade inga resultat.
          </span>
        </div>
      } else { // Search query is empty (eg. on page load)
        r = <TemplateFactory sections={page.sections} />
      }
    } else { // Search result
      r = <div className="container">
        <div className="mx-auto max-w-[1400px]">
          {((selectedCategories.length > 0) || (selectedBrands.length > 0)) &&
            <ul className="mb-12 flex flex-wrap items-center">
              {selectedCategories.map((cat) => (
                <span key={cat.value} onClick={handleSelectFilter}>
                  <Badge key={cat.value} arr={selectedCategories} set={setSelectedCategories} tag={cat} />
                </span>
              ))}
              {selectedBrands.map((cat) => (
                <span key={cat.value} onClick={handleSelectFilter}>
                  <Badge key={cat.value} arr={selectedBrands} set={setSelectedBrands} tag={cat} />
                </span>
              ))}
            </ul>
          }
          {
            searchResults?.showsResult?.shows?.length
              ? <div className="pb-22">
                <h3 className="heading-h3 pb-4">Shows ({searchResults.showsResult.total})</h3>
                <div className="grid grid-cols-1 gap-6 xs:grid-cols-2 sm:grid-cols-4">
                  {searchResults.showsResult.shows.map((show, index) => (
                    <div key={show.id} className="w-full">
                      <ShowCard place={ShowCardPlace.Search} show={show} />
                      {
                        (searchResults.showsResult.shows.length === index + 1)
                          ? <div ref={lastShowRef}></div>
                          : null
                      }
                    </div>
                  ))}
                </div>
                {
                  moreShowsToFetch
                  &&
                  <div className='flex justify-center mt-10'>
                    <button
                      type="button"
                      className={`btn-black btn-large ${isFetching ? "bg-lighterGray" : ""}`}
                      disabled={isFetching}
                      onClick={() => setLoadMore(true)}
                    >
                      <span className="mr-3">
                        {isFetching ? 'Hämtar shows...' : 'Visa fler shows'}
                      </span>
                    </button>
                  </div>
                }
              </div>
              : null
          }

          <div className="pb-16 md:pb-22">
            <h3 className="heading-h3 pb-4">Kategorier ({searchResults.categoriesResult.total})</h3>
            <div className="grid grid-cols-2 gap-6 xs:grid-cols-3 sm:grid-cols-4 md:grid-cols-5">
              {searchResults.categoriesResult.categories.map((category) => (
                <div key={category.name}>
                  <CategoryCard category={category} noImage />
                </div>
              ))}
            </div>
          </div>

          <div className="pb-14 md:pb-22">
            <h3 className="heading-h3 pb-4">Varumärken ({searchResults.brandsResult.total})</h3>
            <div className="grid grid-cols-2 gap-6 xs:grid-cols-3 sm:grid-cols-4 xl:grid-cols-5">
              {searchResults.brandsResult.brands.map((brand) => (
                <div key={brand.name}>
                  <BrandCard {...brand} />
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    }
    return r;
  }

  return (
    <>
      <Layout title={page?.title} meta={page?.meta} isAuthenticated={isAuthenticated}>
        <div className="container pb-12">
          <div className="relative">
            <div className="flex flex-col items-center justify-center bg-rose-300 bg-opacity-40 py-10 md:py-16">
              <h1 className="heading-h1 mb-8">Shows</h1>
              <div className="flex flex-col items-center justify-center space-y-3 mx-3 md:flex-row md:space-x-3 md:space-y-0">
                <form
                  id="filterform"
                  onSubmit={(e) => handleOnSubmit(e)}
                  className="relative space-x-3 sm:min-w-[330px] md:min-w-[456px]"
                  ref={formRef}>
                  <input
                    type="text"
                    id="search"
                    value={searchValue}
                    onChange={({ target }) => {
                      setPageNumber(1)
                      setSearchValue(target.value)
                    }}
                    className="peer h-[60px] w-full rounded-full bg-white px-7 pl-16 text-black outline-none ring-inset focus:ring-2 focus:ring-rose-300"
                    placeholder=" "
                  />
                  <label
                    htmlFor="email"
                    className="pointer-events-none absolute left-12 top-1/2 hidden -translate-y-1/2 text-semiSmall md:text-base font-semibold peer-placeholder-shown:block peer-focus:hidden pr-6">
                    Sök på titel, kategori <br className="sm:hidden" />eller varumärke
                  </label>
                  <div className="absolute left-3 top-1/2 -translate-y-1/2">
                    <Search />
                  </div>
                </form>
                <Dialog
                  ref={dialogRef}
                  maxWidth="4xl"
                  button={
                    <button className="btn-white btn-large relative flex w-full items-center justify-center">
                      <Filter />
                      <span className="ml-3">Filtrera</span>
                      {(!!selectedBrands.length || !!selectedCategories.length) && (
                        <span className="absolute -top-1 -right-1 flex h-[30px] w-[30px] items-center justify-center rounded-full bg-rose-400 text-[14px] leading-none text-white">
                          {selectedBrands.length + selectedCategories.length}
                        </span>
                      )}
                    </button>
                  }>
                  <div className="p-8 pt-12 ">
                    <div className="text-left">
                      <h3 className="heading-h3 mb-4">Filter</h3>
                      <h4 className="heading-h4 mb-2">Kategori</h4>
                      <ul className="mb-6 flex flex-wrap items-center">
                        {searchResults.facets.categories.map((category) => (
                          <span key={category.id} onClick={handleSelectFilter}>
                            <Badge
                              key={category.id}
                              arr={selectedCategories}
                              tag={{ name: category.name, value: category.id, color: category.color }}
                              set={setSelectedCategories}
                            />
                          </span>
                        ))}
                      </ul>
                      <h4 className="heading-h4 mb-2">Varumärke</h4>
                      <ul className="mb-6 flex flex-wrap items-center">
                        {searchResults.facets.brands
                          .slice(0, showAllBrands ? searchResults.facets.brands.length : 10)
                          .map((brand) => (
                            <span key={brand.id} onClick={handleSelectFilter}>
                              <Badge
                                key={brand.id}
                                arr={selectedBrands}
                                tag={{ name: brand.name, value: brand.id, color: '#000000' }}
                                set={setSelectedBrands}
                              />
                            </span>
                          ))}
                        {searchResults.facets.brands.length > 10 && (
                          <li>
                            <button
                              className="px-5 py-3 leading-none"
                              onClick={() => setShowAllBrands((prev) => !prev)}>
                              {showAllBrands ? 'Visa mindre' : 'Visa alla'}
                            </button>
                          </li>
                        )}
                      </ul>
                    </div>
                    <div className="flex items-center justify-end">
                      <button className="btn-text h-16" onClick={handleClearFilters}>
                        Rensa filter
                      </button>
                      <button className="btn-black h-16" onClick={() => dialogRef.current?.setOpen(false)}>
                        Visa resultat
                      </button>
                    </div>
                  </div>
                </Dialog>
              </div>
              <Symbol
                color="#D6B3B4"
                className="absolute left-0 hidden -translate-y-5 -translate-x-20 rotate-[10deg] sm:block"
              />
              <Symbol
                color="#D6B3B4"
                className="absolute right-0 hidden translate-y-16 translate-x-20 rotate-[-90deg] sm:block"
              />
            </div>
          </div>
        </div>
        {showSearchResult()}
        {isFetching &&
          <div className="container pt-16 pb-28 text-center">
            <Spinner size="32" center />
          </div>
        }
      </Layout>
    </>
  );
};

export default ShowsPage;
